import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { defaults } from "@assoanalytics/app/app.config";

import { StorageService } from "@assoanalytics/app/core/services/storage.service";
import { TranslateService } from "@ngx-translate/core";
import { catchError, combineLatest, of, tap } from "rxjs";
import { TokenService } from "@assoanalytics/app/core/services/token.service";
import { EventService } from "@assoanalytics/app/core/services/event.service";
import { UserRole } from "@assoanalytics/app/core/models/user/user-role";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  appDefaults = defaults;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private storageService: StorageService,
    private tokenService: TokenService,
  ) {}

  initTranslateService() {
    // set supported languages
    this.translate.addLangs(Object.keys(defaults.supportedLanguages));

    // set language to use
    if (!this.storageService.getLanguage()) {
      const detectedLang =
        this.translate.getBrowserLang() || defaults.defaultLanguage;
      this.translate.use(detectedLang);
      this.storageService.setLanguage(detectedLang);
    } else {
      this.translate.use(this.storageService.getLanguage()!);
    }
  }
  getCurrentLanguage(): string {
    return (
      this.translate.currentLang ||
      this.translate.getBrowserLang() ||
      this.translate.defaultLang
    );
  }

  changeLanguageForRole(userRole: UserRole) {
    const path = "./assets/i18n";
    const customPath = "./brand/assets/i18n";

    const roles = UserRole;
    const role = roles[userRole].toLowerCase();
    const lang: string = this.translate.currentLang;

    // Ottieni token e genera hash per cache busting
    const token = this.tokenService.getToken() || "";
    const tokenHash = btoa(token);

    // Carico i due file principali con cache busting
    const requests = [
      this.http.get(`${path}/${role}/${lang}.json?cacheBust=${tokenHash}`),
    ];

    // Se customMode è attivo e il file personalizzato esiste, lo carico
    let customTranslation$ = of({}); // Default vuoto

    // Se la lingua non è "it", aggiungo file it come fallback
    let fallbackTranslation$ = of({}); // Default vuoto
    if (lang !== "it") {
      fallbackTranslation$ = this.http
        .get(`${path}/${role}/it.json?cacheBust=${tokenHash}`)
        .pipe(
          catchError(() => of({})), // Se il file non esiste, restituisce un oggetto vuoto
        );
    }

    combineLatest([...requests, customTranslation$, fallbackTranslation$])
      .pipe(
        tap(() => {
          EventService.overlayShow.next({
            loading: true,
            message: "OVERLAY_COMPONENT.LOADING",
          });
        }),
      )
      .subscribe({
        next: ([
          roleTranslations, // file ruolo default
          customTranslations, // file ruolo brand
          fallbackTranslations, // file ita per fallback lingua straniera
        ]) => {
          // Funzione per aggiungere solo le chiavi mancanti
          function addMissingKeys(target: any, source: any) {
            for (const key in source) {
              if (!target.hasOwnProperty(key)) {
                target[key] = source[key]; // Aggiunge solo se non esiste già
              } else if (
                typeof source[key] === "object" &&
                typeof target[key] === "object"
              ) {
                // Ricorsione per gli oggetti annidati
                addMissingKeys(target[key], source[key]);
              }
            }
          }

          // Funzione per sovrascrivere valori
          function overrideKeys(target: any, source: any) {
            for (const key in source) {
              if (
                source[key] &&
                typeof source[key] === "object" &&
                typeof target[key] === "object"
              ) {
                // Fusione ricorsiva per oggetti annidati
                overrideKeys(target[key], source[key]);
              } else {
                // Sovrascrive chiavi semplici o non comuni
                target[key] = source[key];
              }
            }
          }

          // Sovrascrivo con le chiavi del brand
          overrideKeys(roleTranslations, customTranslations);

          // Merge solo delle chiavi mancanti
          addMissingKeys(roleTranslations, fallbackTranslations);

          // 2. Merge completo del file custom (sovrascrive le chiavi esistenti)
          // const finalTranslations = {
          //   ...roleTranslations,
          //   ...customTranslations
          // };

          // Imposto la traduzione aggiornata
          this.translate.setTranslation(lang, roleTranslations, true);
        },
        complete: () => {
          EventService.overlayShow.next({ loading: false });
        },
      });
  }
}
